import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Film, Loader2, Play, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ClipPublishingControls } from '@/components/publishing/ClipPublishingControls';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { usePostableClipExpiry } from '@/hooks/use-postable-clip-expiry';
import { isPostableClip } from '@/lib/publishing/isPostableClip';
import type { PublishPlatform } from '@/lib/publishing/types';
import { MY_POSTABLE_CLIPS } from '@/queries/scheduledPublishing';

const PAGE_SIZE = 24;

export type PostableClip = {
  id: string;
  name?: string | null;
  thumbnail_url?: string | null;
  finished_video_url?: string | null;
  createdAt?: string | null;
  clipping_status?: string | null;
  preparing_status?: string | null;
  rendering_status?: string | null;
  processing_status?: string | null;
};

export interface CalendarClipPick {
  clipId: string;
  clipTitle: string;
  platform: PublishPlatform;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dayKey: string | null;
  onPick: (pick: CalendarClipPick) => void;
}

function formatDayLabel(dayKey: string, language: string): string {
  const [year, month, day] = dayKey.split('-').map(Number);
  if (!year || !month || !day) return dayKey;
  return new Intl.DateTimeFormat(language, {
    dateStyle: 'long',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, day, 12)));
}

export const CalendarClipPickerDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  dayKey,
  onPick,
}) => {
  const { t, i18n } = useTranslation();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingMoreRef = useRef(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setDebouncedSearch('');
    setHasMore(true);
  }, [open, dayKey]);

  useEffect(() => {
    setHasMore(true);
  }, [debouncedSearch]);

  const maxExpiryDays = usePostableClipExpiry(open);

  const { data, loading, fetchMore } = useQuery(MY_POSTABLE_CLIPS, {
    variables: {
      search: debouncedSearch || undefined,
      limit: PAGE_SIZE,
      offset: 0,
    },
    skip: !open,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const fetchedClips = useMemo(
    () => ((data?.myPostableClips || []) as PostableClip[]),
    [data?.myPostableClips],
  );
  const clips = useMemo(
    () => fetchedClips.filter((clip) => isPostableClip(clip, { maxExpiryDays, now: new Date() })),
    [fetchedClips, maxExpiryDays],
  );

  useEffect(() => {
    if (!loading && fetchedClips.length < PAGE_SIZE) setHasMore(false);
  }, [loading, fetchedClips.length]);

  const handleLoadMore = async () => {
    if (loadingMoreRef.current || loading || !hasMore) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const result = await fetchMore({
        variables: {
          search: debouncedSearch || undefined,
          limit: PAGE_SIZE,
          offset: fetchedClips.length,
        },
        updateQuery: (previous, { fetchMoreResult }) => {
          const next = fetchMoreResult?.myPostableClips ?? [];
          return {
            myPostableClips: [...(previous.myPostableClips ?? []), ...next],
          };
        },
      });
      const fetched = result.data?.myPostableClips?.length ?? 0;
      if (fetched < PAGE_SIZE) setHasMore(false);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  };

  const pick = (clip: PostableClip, platform: PublishPlatform) => {
    onPick({
      clipId: clip.id,
      clipTitle: clip.name || '',
      platform,
    });
  };

  const dateLabel = dayKey ? formatDayLabel(dayKey, i18n.language) : '';
  const isInitialLoading = loading && clips.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="calendar-clip-picker"
        className="flex h-[85dvh] max-h-[85dvh] max-w-3xl flex-col gap-4 overflow-hidden sm:max-w-4xl"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{t('publishing.calendar.pickClipTitle')}</DialogTitle>
          <DialogDescription>
            {t('publishing.calendar.pickClipDescription', { date: dateLabel })}
          </DialogDescription>
        </DialogHeader>

        <div className="relative shrink-0">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="calendar-clip-search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('publishing.schedule.clipSearch')}
            className="pl-9"
            aria-label={t('publishing.schedule.clipSearch')}
          />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          {isInitialLoading ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {Array.from({ length: 8 }).map((_, index) => (
                <div key={index} className="animate-pulse rounded-lg border bg-muted/40">
                  <div className="aspect-[9/16] rounded-t-lg bg-muted" />
                  <div className="space-y-2 p-2">
                    <div className="h-3 w-3/4 rounded bg-muted" />
                    <div className="h-8 rounded bg-muted" />
                  </div>
                </div>
              ))}
            </div>
          ) : clips.length === 0 ? (
            <p className="px-2 py-8 text-center text-sm text-muted-foreground" data-testid="calendar-clip-empty">
              {t('publishing.schedule.clipEmpty')}
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {clips.map((clip) => (
                <CalendarClipPreviewCard
                  key={clip.id}
                  clip={clip}
                  onPick={(platform) => pick(clip, platform)}
                />
              ))}
            </div>
          )}
          {hasMore && clips.length > 0 ? (
            <div className="flex justify-center py-4">
              <Button
                type="button"
                variant="outline"
                data-testid="calendar-clip-load-more"
                onClick={() => { void handleLoadMore(); }}
                disabled={loadingMore}
              >
                {loadingMore && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('publishing.calendar.loadMore')}
              </Button>
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
};

function CalendarClipPreviewCard({
  clip,
  onPick,
}: {
  clip: PostableClip;
  onPick: (platform: PublishPlatform) => void;
}) {
  const { t } = useTranslation();
  const [previewing, setPreviewing] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const title = clip.name || t('publishing.calendar.untitled');
  const canPlay = !!clip.finished_video_url;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (previewing) {
      try {
        const playing = video.play();
        if (playing && typeof playing.catch === 'function') {
          void playing.catch(() => undefined);
        }
      } catch {
        // jsdom and autoplay-blocked browsers leave the still frame.
      }
      return;
    }
    video.pause();
    video.currentTime = 0;
  }, [previewing]);

  return (
    <article
      data-testid={`calendar-clip-${clip.id}`}
      className="overflow-hidden rounded-lg border bg-card"
    >
      <button
        type="button"
        data-testid={`calendar-clip-preview-${clip.id}`}
        className="relative block aspect-[9/16] w-full overflow-hidden bg-muted"
        aria-label={t('publishing.calendar.clipPreview', { name: title })}
        aria-pressed={previewing}
        onMouseEnter={() => { if (canPlay) setPreviewing(true); }}
        onMouseLeave={() => setPreviewing(false)}
        onFocus={() => { if (canPlay) setPreviewing(true); }}
        onBlur={() => setPreviewing(false)}
        onClick={() => { if (canPlay) setPreviewing((current) => !current); }}
      >
        {clip.thumbnail_url ? (
          <img
            src={clip.thumbnail_url}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : (
          <span className="absolute inset-0 flex items-center justify-center text-muted-foreground">
            <Film className="h-8 w-8" aria-hidden="true" />
          </span>
        )}
        {canPlay && previewing ? (
          <video
            ref={videoRef}
            src={clip.finished_video_url ?? undefined}
            muted
            loop
            playsInline
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : null}
        {canPlay && !previewing ? (
          <span className="absolute inset-0 flex items-center justify-center bg-black/20">
            <span className="rounded-full bg-background/90 p-2 text-foreground shadow-sm">
              <Play className="h-4 w-4 fill-current" aria-hidden="true" />
            </span>
          </span>
        ) : null}
      </button>
      <div className="space-y-2 p-2">
        <p className="truncate text-xs font-medium" title={title}>{title}</p>
        <ClipPublishingControls
          clipId={clip.id}
          canPublish
          compact
          showCarouselAction={false}
          onSendToTikTok={() => onPick('tiktok')}
          onPublishToInstagram={() => onPick('instagram')}
          onPublishToFacebook={() => onPick('facebook')}
          onPublishToYouTube={() => onPick('youtube')}
        />
      </div>
    </article>
  );
}

export default CalendarClipPickerDialog;
